<template>
  <teleport to="body">
    <pdl-drawer
      :style="headerStyle"
      :is-open="isDrawerOpen"
      :size="getDrawerWidth"
      :modal="false"
      qaid="color-selector-drawer"
      @keydown.esc="$emit('close-drawer-no-confirmation')"
    >
      <template #content>
        <div class="pl-2 pt-2 pb-2 justify-start bg-gray-02">
          <pdl-button
            small
            tertiary
            text
            icon="chevron_left"
            qaid="color-selector-drawer-back-button"
            @click="$emit('close-drawer-no-confirmation')"
          >
            {{ $t('text.discard') }}
          </pdl-button>
        </div>

        <div class="flex justify-between p-2 pt-0 bg-gray-02 w-full" qaid="color-selector-drawer-component">
          <h2 class="text-md font-bold">
            {{ schemeDescription }}
          </h2>
          <span class="text-sm"> {{ formattedPriceRange }} </span>
        </div>

        <pdl-loading v-if="isLoadingColors" :is-loading="isLoadingColors" class="mt-2" />
        <div v-else class="absolute top-30 bottom-12 left-0 right-0 overflow-y-auto mb-3">
          <paint-scheme-options-list v-if="isYouDesignTheme" class="pl-2" />
          <div v-else class="grid grid-cols-2 gap-2 px-1">
            <inspiration-card
              v-for="inspiration in themeInspirations"
              :key="inspiration.pk"
              :inspiration="inspiration"
              @inspiration-clicked="handleInspirationClicked"
            />
          </div>
        </div>

        <div class="absolute bottom-3 left-4 right-4 mt-3">
          <pdl-button
            block
            qaid="color-selector-drawer-confirm-button"
            :disabled="isLoadingColors"
            @click="$emit('confirmed-selections')"
          >
            {{ $t('product.fdp.customizable.config.confirmSelection') }}
          </pdl-button>
        </div>
      </template>
    </pdl-drawer>
  </teleport>
</template>

<script>
import {PdlButton} from '@pedal/pdl-button';
import {PdlDrawer} from '@pedal/pdl-drawer';
import {mapState, mapGetters, mapActions} from 'vuex';
import PaintSchemeOptionsList from '@/components/containers/buy-zone/customization/PaintSchemeOptionsList.vue';
import {PdlLoading} from '@pedal/pdl-loading';
import InspirationCard from '@/components/containers/buy-zone/customization/InspirationCard.vue';
import {GROUP_IDS} from '@/familyDetailsPage/store/bike-customization';
import {formatThemeInspirationsPriceRange} from '@/familyDetailsPage/helpers/formatInspirationPriceRange.js';

export default {
  name: 'ColorSelectorDrawer',
  components: {
    PdlButton,
    PdlDrawer,
    PdlLoading,
    PaintSchemeOptionsList,
    InspirationCard,
  },
  props: {
    isDrawerOpen: {
      type: Boolean,
      default: false,
    },
    scheme: {
      type: Object,
      default: () => ({}),
    },
    isLoadingColors: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close-drawer-no-confirmation', 'confirmed-selections'],
  computed: {
    ...mapState('global', ['isCompactView']),
    ...mapState('bikeCustomization', ['themeOptions', 'paintSchemeOptions']),
    ...mapGetters('bikeCustomization', ['selections']),

    getDrawerWidth() {
      return !this.isCompactView ? '30rem' : '100%';
    },
    headerStyle() {
      return {
        '--customization-img-top': 'min(35vh, calc(100vw * 9 / 16))',
      };
    },
    schemeDescription() {
      return this.scheme?.marketingThemeGroup || '';
    },
    isYouDesignTheme() {
      // 'You design' themes have group options to customize (like Body Color id 99)
      // vs 'We design' themes which are missing color group options
      return this.paintSchemeOptions?.some((group) => group.id === GROUP_IDS.BODY_COLOR);
    },
    themeInspirations() {
      return this.themeOptions?.find((theme) => theme.id === this.scheme?.id)?.inspirations || [];
    },
    formattedPriceRange() {
      const currentTheme = this.themeOptions?.find((theme) => theme.id === this.scheme?.id) ?? {};
      return formatThemeInspirationsPriceRange(currentTheme);
    },
  },
  methods: {
    ...mapActions('bikeCustomization', ['updateSelection']),

    handleInspirationClicked(inspiration) {
      this.updateSelection({
        selections: this.selections,
        module: 'paint-scheme',
        presetPK: inspiration?.pk,
        forceGet: true,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.pdl-drawer {
  z-index: 9997;
  top: calc(var(--customization-img-top) + var(--header-height));
  height: calc(100dvh - var(--customization-img-top) - var(--header-height));

  @media (screen(lg)) {
    top: var(--header-height);
    height: calc(100% - var(--header-height));
  }
}

.pdl-drawer--open {
  :deep(.pdl-drawer__content-wrapper) {
    @media (screen(lg)) {
      box-shadow: -10px 0 50px 0 rgba(0, 0, 0, 0.29);
    }
  }
}

.pdl-drawer--right {
  --max-width: 100%;
}

:deep(.pdl-drawer__content) {
  padding: 0;
}
</style>
